import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
    getAuth, 
    signInAnonymously, 
    onAuthStateChanged,
    signInWithCustomToken
} from 'firebase/auth';
import { 
    getFirestore, 
    collection, 
    addDoc, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp,
    query,
    where,
    orderBy,
    limit,
    setDoc
} from 'firebase/firestore';

// Tailwind CSS를 위한 CDN 링크 (HTML 파일 <head>에 추가 필요)
// <script src="https://cdn.tailwindcss.com"></script>

// Firebase 구성 (실제 값으로 대체 필요)
// const firebaseConfig = { /* YOUR_FIREBASE_CONFIG */ };
// 위 firebaseConfig는 __firebase_config 전역 변수로 대체됩니다.

// --- 아이콘 SVG ---
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clipRule="evenodd" />
  </svg>
);

const PaperAirplaneIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z" />
  </svg>
);

const PlusCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 9a.75.75 0 0 0-1.5 0v2.25H9a.75.75 0 0 0 0 1.5h2.25V15a.75.75 0 0 0 1.5 0v-2.25H15a.75.75 0 0 0 0-1.5h-2.25V9Z" clipRule="evenodd" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-green-500">
    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm13.36-1.814a.75.75 0 1 0-1.06-1.06l-3.093 3.093-1.407-1.407a.75.75 0 0 0-1.06 1.06l1.937 1.937a.75.75 0 0 0 1.06 0l3.623-3.623Z" clipRule="evenodd" />
  </svg>
);

const ArrowUpCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm.75 6.75a.75.75 0 0 0-1.5 0v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V9Z" clipRule="evenodd" />
  </svg>
);


function App() {
  const [db, setDb] = useState(null);
  const [auth, setAuth] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const [services, setServices] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [userVotes, setUserVotes] = useState({}); // { serviceId: true }

  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDescription, setNewServiceDescription] = useState('');
  const [newServiceUrl, setNewServiceUrl] = useState('');
  const [newServicePrompt, setNewServicePrompt] = useState('');
  const [newChatMessage, setNewChatMessage] = useState('');
  
  // 사용자 이름 관리
  const [userName, setUserName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [tempUserName, setTempUserName] = useState('');
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [isLoadingChat, setIsLoadingChat] = useState(true);
  const [error, setError] = useState(null);
  const [isAddServiceModalOpen, setIsAddServiceModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('services'); // 'services' 또는 'chat'

  const chatEndRef = useRef(null);

  // --- Firebase 앱 ID 및 설정 가져오기 ---
  const appId = typeof window.__app_id !== 'undefined' ? window.__app_id : (process.env.REACT_APP_APP_ID || 'default-app-id');
  
  useEffect(() => {
    try {
      const firebaseConfigStr = typeof window.__firebase_config !== 'undefined' ? window.__firebase_config : null;
      
      // 환경 변수에서 Firebase 설정 가져오기
      const envConfig = {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID
      };
      
      let firebaseConfig;
      if (firebaseConfigStr) {
        firebaseConfig = JSON.parse(firebaseConfigStr);
      } else if (envConfig.apiKey && envConfig.projectId) {
        firebaseConfig = envConfig;
      } else {
        console.error("Firebase config not found in global variables or environment variables.");
        setError("Firebase 설정 정보를 찾을 수 없습니다. .env.local 파일에 Firebase 설정을 추가하거나 관리자에게 문의하세요.");
        setIsAuthReady(true);
        return;
      }
      
      const app = initializeApp(firebaseConfig);
      const firestoreDb = getFirestore(app);
      const firebaseAuth = getAuth(app);
      
      setDb(firestoreDb);
      setAuth(firebaseAuth);      const unsubscribeAuth = onAuthStateChanged(firebaseAuth, async (currentUser) => {
        if (currentUser) {
          setUserId(currentUser.uid);
        } else {
          // 사용자 정보가 없으면 익명 로그인 또는 커스텀 토큰 로그인 시도
          try {
            if (typeof window.__initial_auth_token !== 'undefined' && window.__initial_auth_token) {
              await signInWithCustomToken(firebaseAuth, window.__initial_auth_token);
            } else {
              await signInAnonymously(firebaseAuth);
            }
          } catch (authError) {
            console.error("Error during sign-in:", authError);
            setError("인증에 실패했습니다: " + authError.message);
          }
        }
        setIsAuthReady(true);
      });
      return () => unsubscribeAuth();
    } catch (e) {
      console.error("Firebase initialization error:", e);
      setError("Firebase 초기화 중 오류가 발생했습니다: " + e.message);
      setIsAuthReady(true); 
    }
  }, []);


  // --- Firestore 경로 설정 ---
  const servicesCollectionPath = `artifacts/${appId}/public/data/services`;
  const votesCollectionPath = `artifacts/${appId}/public/data/votes`;
  const chatMessagesCollectionPath = `artifacts/${appId}/public/data/chatMessages`;
  const usersCollectionPath = `artifacts/${appId}/public/data/users`;

  // --- 사용자 이름 로드 ---
  useEffect(() => {
    if (!db || !userId) return;
    
    const userDocRef = doc(db, usersCollectionPath, userId);
    const unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        setUserName(userData.displayName || `익명${userId.substring(0, 5)}`);
      } else {
        // 사용자 문서가 없으면 기본 이름으로 생성
        const defaultName = `익명${userId.substring(0, 5)}`;
        setUserName(defaultName);
        setDoc(userDocRef, {
          displayName: defaultName,
          createdAt: serverTimestamp()
        }).catch(err => console.error("Error creating user document:", err));
      }
    });

    return () => unsubscribeUser();
  }, [db, userId, usersCollectionPath]);

  // --- 서비스 목록 및 사용자 투표 현황 불러오기 ---
  useEffect(() => {
    if (!db || !isAuthReady || !userId) {
      if (isAuthReady && !userId) console.log("Auth is ready, but no user ID yet for fetching services/votes.");
      return;
    }
    
    setIsLoadingServices(true);
    // 서비스 목록 실시간 감지
    const servicesQuery = query(collection(db, servicesCollectionPath));
    const unsubscribeServices = onSnapshot(servicesQuery, (snapshot) => {
      const fetchedServices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // voteCount가 없는 경우 0으로 초기화 (데이터 일관성)
      const servicesWithVoteCount = fetchedServices.map(s => ({ ...s, voteCount: s.voteCount || 0 }));
      setServices(servicesWithVoteCount);
      setIsLoadingServices(false);
    }, (err) => {
      console.error("Error fetching services:", err);
      setError("서비스 목록을 불러오는 중 오류가 발생했습니다.");
      setIsLoadingServices(false);
    });

    // 사용자 투표 현황 실시간 감지
    const userVotesQuery = query(collection(db, votesCollectionPath), where("userId", "==", userId));
    const unsubscribeUserVotes = onSnapshot(userVotesQuery, (snapshot) => {
      const votes = {};
      snapshot.docs.forEach(doc => {
        votes[doc.data().serviceId] = true;
      });
      setUserVotes(votes);
    }, (err) => {
      console.error("Error fetching user votes:", err);
      setError("사용자 투표 정보를 불러오는 중 오류가 발생했습니다.");
    });

    return () => {
      unsubscribeServices();
      unsubscribeUserVotes();
    };
  }, [db, isAuthReady, userId, servicesCollectionPath, votesCollectionPath]);

  // --- 채팅 메시지 불러오기 ---
  useEffect(() => {
    if (!db || !isAuthReady) return;
    
    setIsLoadingChat(true);
    const chatQuery = query(collection(db, chatMessagesCollectionPath), orderBy("timestamp", "asc"), limit(50));
    const unsubscribeChat = onSnapshot(chatQuery, (snapshot) => {
      const messages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setChatMessages(messages);
      setIsLoadingChat(false);
    }, (err) => {
      console.error("Error fetching chat messages:", err);
      setError("채팅 메시지를 불러오는 중 오류가 발생했습니다.");
      setIsLoadingChat(false);
    });

    return () => unsubscribeChat();
  }, [db, isAuthReady, chatMessagesCollectionPath]);

  // 채팅 스크롤 맨 아래로
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // --- 웹 서비스 등록 핸들러 ---
  const handleAddService = async (e) => {
    e.preventDefault();
    if (!db || !newServiceName.trim() || !newServiceUrl.trim() || !userId) {
      alert("서비스 이름과 URL을 모두 입력해주세요.");
      return;
    }
    // URL 유효성 검사 (간단한 http/https 시작 검사)
    if (!newServiceUrl.startsWith('http://') && !newServiceUrl.startsWith('https://')) {
        alert("URL은 http:// 또는 https:// 로 시작해야 합니다.");
        return;
    }

    try {
      await addDoc(collection(db, servicesCollectionPath), {
        name: newServiceName,
        description: newServiceDescription.trim() || '설명이 제공되지 않았습니다.',
        url: newServiceUrl,
        prompt: newServicePrompt.trim() || '프롬프트가 제공되지 않았습니다.',
        voteCount: 0,
        createdAt: serverTimestamp(),
        registeredBy: userId,
        registeredByName: userName
      });
      setNewServiceName('');
      setNewServiceDescription('');
      setNewServiceUrl('');
      setNewServicePrompt('');
      setIsAddServiceModalOpen(false);
    } catch (err) {
      console.error("Error adding service:", err);
      alert("서비스 등록 중 오류 발생: " + err.message);
    }
  };

  // --- 사용자 이름 수정 핸들러 ---
  const handleEditUserName = () => {
    setTempUserName(userName);
    setIsEditingName(true);
  };

  const handleSaveUserName = async () => {
    if (!db || !userId || !tempUserName.trim()) return;
    
    try {
      const userDocRef = doc(db, usersCollectionPath, userId);
      await updateDoc(userDocRef, {
        displayName: tempUserName.trim(),
        updatedAt: serverTimestamp()
      });
      setIsEditingName(false);
      setTempUserName('');
    } catch (err) {
      console.error("Error updating user name:", err);
      alert("이름 수정 중 오류 발생: " + err.message);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setTempUserName('');
  };

  // --- 투표 핸들러 ---
  const handleVote = async (serviceId) => {
    if (!db || !userId || userVotes[serviceId]) {
      alert(userVotes[serviceId] ? "이미 이 서비스에 투표하셨습니다." : "투표하려면 먼저 인증해야 합니다.");
      return;
    }

    const serviceRef = doc(db, servicesCollectionPath, serviceId);
    const voteDocId = `${userId}_${serviceId}`; // 사용자 ID와 서비스 ID를 조합하여 고유한 문서 ID 생성
    const voteRef = doc(db, votesCollectionPath, voteDocId);

    try {
      // 1. votes 컬렉션에 투표 기록 (setDoc으로 고유 ID 사용)
      await setDoc(voteRef, {
        userId: userId,
        serviceId: serviceId,
        timestamp: serverTimestamp()
      });

      // 2. services 컬렉션의 voteCount 업데이트 (기존 값에서 +1)
      //    원자적 업데이트를 위해선 transaction 또는 increment 사용이 좋으나,
      //    간단하게 현재 서비스 정보를 가져와서 업데이트합니다.
      const currentService = services.find(s => s.id === serviceId);
      if (currentService) {
        await updateDoc(serviceRef, {
          voteCount: (currentService.voteCount || 0) + 1
        });
      } else {
        console.warn("Service not found for voting, voteCount not updated on service document.");
      }
      // 로컬 상태 업데이트 (onSnapshot이 처리하지만 즉각적인 UI 반응을 위해)
      setUserVotes(prev => ({ ...prev, [serviceId]: true }));

    } catch (err) {
      console.error("Error voting:", err);
      // 이미 투표한 경우 (setDoc이 실패하지 않음, Firestore 규칙으로 막아야 함)
      // Firestore 규칙이 `allow create: if request.auth.uid == request.resource.data.userId && !exists(/databases/$(database)/documents/$(votesCollectionPath)/$(request.auth.uid)_$(request.resource.data.serviceId));`
      // 와 같이 설정되어 있다면, 여기서 에러가 발생할 수 있습니다.
      // 현재는 클라이언트에서 한 번 더 체크하고, setDoc으로 덮어쓰기 때문에 Firestore 규칙이 중요합니다.
      alert("투표 처리 중 오류 발생: " + err.message);
    }
  };

  // --- 채팅 메시지 전송 핸들러 ---
  const handleSendChatMessage = async (e) => {
    e.preventDefault();
    if (!db || !newChatMessage.trim() || !userId) return;

    try {
      await addDoc(collection(db, chatMessagesCollectionPath), {
        text: newChatMessage,
        userId: userId, // 익명 사용자의 경우 uid
        userName: userName || `익명${userId.substring(0, 5)}`, // 사용자 설정 이름 사용
        timestamp: serverTimestamp()
      });
      setNewChatMessage('');
    } catch (err) {
      console.error("Error sending chat message:", err);
      alert("메시지 전송 중 오류 발생: " + err.message);
    }
  };

  // --- 로딩 및 에러 처리 ---
  if (!isAuthReady) {
    return <div className="flex items-center justify-center h-screen text-xl font-semibold">인증 정보를 확인 중입니다...</div>;
  }
  if (error) {
    return <div className="flex items-center justify-center h-screen text-red-500 text-xl p-8">{error}</div>;
  }
  if (!db || !auth) {
    return <div className="flex items-center justify-center h-screen text-xl font-semibold">Firebase 초기화 중...</div>;
  }

  const totalVotes = services.reduce((sum, service) => sum + (service.voteCount || 0), 0);    return (
    <div className="flex flex-col h-screen bg-gray-900 text-white font-sans">
      {/* 상단: 현황판 */}
      <header className="bg-gray-800 p-3 sm:p-6 shadow-md border-b border-gray-700">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center text-blue-400 mb-3 sm:mb-4">
          VIBE 코딩 세미나: 실시간 투표 서비스
        </h1>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="bg-gray-700 p-2 sm:p-4 rounded-lg shadow">
            <h3 className="text-sm sm:text-lg font-semibold text-blue-300">등록된 서비스</h3>
            <p className="text-2xl sm:text-4xl font-bold">{isLoadingServices ? '...' : services.length}</p>
          </div>
          <div className="bg-gray-700 p-2 sm:p-4 rounded-lg shadow">
            <h3 className="text-sm sm:text-lg font-semibold text-blue-300">총 투표 수</h3>
            <p className="text-2xl sm:text-4xl font-bold">{isLoadingServices ? '...' : totalVotes}</p>
          </div>
          <div className="col-span-2 lg:col-span-1 flex items-center justify-center">
            <button 
              onClick={() => setIsAddServiceModalOpen(true)}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 sm:py-3 sm:px-6 rounded-lg transition duration-150 flex items-center text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              <PlusCircleIcon />
              <span className="ml-2">새 서비스 등록</span>
            </button>
          </div>
        </div>
      </header>

      {/* 탭 네비게이션 */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="flex">
          <button
            onClick={() => setActiveTab('services')}
            className={`flex-1 py-3 px-4 text-center font-medium transition-colors duration-200 ${
              activeTab === 'services'
                ? 'bg-gray-700 text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            웹 서비스 목록
          </button>
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-3 px-4 text-center font-medium transition-colors duration-200 ${
              activeTab === 'chat'
                ? 'bg-gray-700 text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-300 hover:text-white hover:bg-gray-700'
            }`}
          >
            실시간 Q&A / 채팅
          </button>
        </div>
      </div>

      {/* 메인 콘텐츠 영역 */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'services' ? (
          /* 서비스 목록 탭 */
          <main className="h-full p-3 sm:p-6 overflow-y-auto">
            <h2 className="text-lg sm:text-2xl font-semibold mb-4 sm:mb-6 text-blue-300">투표할 웹 서비스 목록</h2>
            {isLoadingServices && <p className="text-center text-gray-400">서비스 목록을 불러오는 중입니다...</p>}
            {!isLoadingServices && services.length === 0 && (
              <p className="text-center text-gray-400 text-base sm:text-lg">아직 등록된 서비스가 없습니다. 첫 번째 서비스를 등록해보세요!</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
              {services.sort((a,b) => (b.voteCount || 0) - (a.voteCount || 0)).map(service => (
                <div key={service.id} className="bg-gray-800 p-4 sm:p-6 rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg sm:text-xl font-semibold text-blue-400 mb-2 truncate" title={service.name}>{service.name}</h3>
                    <a 
                      href={service.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs sm:text-sm text-blue-500 hover:text-blue-400 break-all hover:underline block"
                    >
                      {service.url}
                    </a>
                    
                    {/* 서비스 설명 */}
                    {service.description && service.description !== '설명이 제공되지 않았습니다.' && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-400 mb-1">설명:</p>
                        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{service.description}</p>
                      </div>
                    )}
                    
                    {/* 프롬프트 정보 */}
                    {service.prompt && service.prompt !== '프롬프트가 제공되지 않았습니다.' && (
                      <div className="mt-3">
                        <p className="text-xs text-gray-400 mb-1">사용된 프롬프트:</p>
                        <div className="bg-gray-700 p-2 rounded text-xs text-gray-300 max-h-16 sm:max-h-20 overflow-y-auto">
                          {service.prompt}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-gray-400 text-xs mt-3 mb-3">
                      등록자: {service.registeredByName || (service.registeredBy ? `...${service.registeredBy.substring(service.registeredBy.length - 5)}` : '알 수 없음')}
                    </p>
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-0">
                    <p className="text-xl sm:text-2xl font-bold text-yellow-400">{service.voteCount || 0} 표</p>
                    {userVotes[service.id] ? (
                      <span className="flex items-center text-green-400 bg-green-800 px-3 py-2 rounded-lg text-xs sm:text-sm font-medium">
                        <CheckCircleIcon /> <span className="ml-2">투표 완료</span>
                      </span>
                    ) : (
                      <button
                        onClick={() => handleVote(service.id)}
                        disabled={!userId}
                        className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg transition duration-150 flex items-center text-xs sm:text-sm w-full sm:w-auto justify-center"
                      >
                        <ArrowUpCircleIcon /> <span className="ml-2">투표하기</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </main>
        ) : (
          /* 채팅 탭 */
          <div className="h-full bg-gray-800 flex flex-col p-3 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-blue-400">실시간 Q&A / 채팅</h2>
            
            {/* 사용자 이름 수정 섹션 */}
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-gray-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm text-gray-300">내 이름:</span>
                {!isEditingName && (
                  <button 
                    onClick={handleEditUserName}
                    className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                  >
                    수정
                  </button>
                )}
              </div>
              {isEditingName ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={tempUserName}
                    onChange={(e) => setTempUserName(e.target.value)}
                    className="w-full p-2 text-xs sm:text-sm rounded bg-gray-600 border border-gray-500 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="새 이름 입력"
                  />
                  <div className="flex space-x-2">
                    <button 
                      onClick={handleSaveUserName}
                      className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded flex-1"
                    >
                      저장
                    </button>
                    <button 
                      onClick={handleCancelEditName}
                      className="text-xs bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded flex-1"
                    >
                      취소
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-blue-300 font-medium text-xs sm:text-sm truncate">{userName}</div>
              )}
            </div>
            
            <div className="flex-grow overflow-y-auto mb-3 sm:mb-4 p-3 bg-gray-700 rounded-lg shadow min-h-0">
              {isLoadingChat && <p className="text-gray-400 text-xs sm:text-sm">채팅 메시지 로딩 중...</p>}
              {!isLoadingChat && chatMessages.length === 0 && <p className="text-gray-400 text-xs sm:text-sm">아직 메시지가 없습니다. 첫 메시지를 남겨보세요!</p>}
              {chatMessages.map(msg => (
                <div key={msg.id} className={`mb-3 p-3 rounded-lg shadow ${msg.userId === userId ? 'bg-blue-600 ml-auto' : 'bg-gray-600 mr-auto'}`} style={{maxWidth: '80%'}}>
                  <p className="text-xs text-gray-300 mb-1 flex items-center">
                    <UserIcon /> <span className="ml-1 truncate">{msg.userName || `익명 ${msg.userId ? msg.userId.substring(0,5) : '알수없음'}`}</span>
                  </p>
                  <p className="text-xs sm:text-sm break-words">{msg.text}</p>
                  <p className="text-xs text-gray-400 mt-1 text-right">
                    {msg.timestamp?.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            
            <form onSubmit={handleSendChatMessage} className="flex">
              <input
                type="text"
                value={newChatMessage}
                onChange={(e) => setNewChatMessage(e.target.value)}
                placeholder="메시지 입력..."
                className="flex-grow p-2 sm:p-3 rounded-l-lg bg-gray-700 border border-gray-600 focus:ring-blue-500 focus:border-blue-500 outline-none text-xs sm:text-sm"
              />
              <button type="submit" className="bg-blue-500 hover:bg-blue-600 text-white p-2 sm:p-3 rounded-r-lg transition duration-150 flex items-center justify-center">
                <PaperAirplaneIcon />
              </button>
            </form>
            
            {userId && (
              <div className="text-xs text-gray-500 mt-3 space-y-1">
                <p className="truncate">ID: {userId}</p>
                <p className="truncate">이름: {userName}</p>
              </div>
            )}
          </div>
        )}
      </div>{/* 서비스 등록 모달 */}
      {isAddServiceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-blue-300">새로운 웹 서비스 등록</h2>
              <button 
                onClick={() => setIsAddServiceModalOpen(false)}
                className="text-gray-400 hover:text-white text-2xl p-1"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddService} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-1">
                  <label htmlFor="serviceName" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">서비스 이름 *</label>
                  <input
                    id="serviceName"
                    type="text"
                    value={newServiceName}
                    onChange={(e) => setNewServiceName(e.target.value)}
                    placeholder="예: 나의 멋진 프로젝트"
                    className="w-full p-2 sm:p-3 rounded-lg bg-gray-700 border border-gray-600 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="serviceUrl" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">서비스 URL *</label>
                  <input
                    id="serviceUrl"
                    type="url"
                    value={newServiceUrl}
                    onChange={(e) => setNewServiceUrl(e.target.value)}
                    placeholder="예: http://192.168.1.101:3000"
                    className="w-full p-2 sm:p-3 rounded-lg bg-gray-700 border border-gray-600 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                    required
                  />
                </div>
              </div>
              <div>
                <label htmlFor="serviceDescription" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">서비스 설명</label>
                <textarea
                  id="serviceDescription"
                  value={newServiceDescription}
                  onChange={(e) => setNewServiceDescription(e.target.value)}
                  placeholder="이 서비스가 어떤 기능을 하는지 간단히 설명해주세요..."
                  rows="3"
                  className="w-full p-2 sm:p-3 rounded-lg bg-gray-700 border border-gray-600 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-sm"
                />
              </div>
              <div>
                <label htmlFor="servicePrompt" className="block text-xs sm:text-sm font-medium text-gray-300 mb-1">사용한 프롬프트</label>
                <textarea
                  id="servicePrompt"
                  value={newServicePrompt}
                  onChange={(e) => setNewServicePrompt(e.target.value)}
                  placeholder="이 서비스를 만들 때 사용한 AI 프롬프트를 공유해주세요..."
                  rows="4"
                  className="w-full p-2 sm:p-3 rounded-lg bg-gray-700 border border-gray-600 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none text-sm"
                />
              </div>
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button 
                  type="button"
                  onClick={() => setIsAddServiceModalOpen(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-150 text-sm sm:text-base"
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition duration-150 flex items-center justify-center text-sm sm:text-base"
                >
                  <PlusCircleIcon /> <span className="ml-2">등록하기</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
